import { Keyboard, Minus, Plus } from 'lucide-react';
import { useState } from 'react';
import { useHaptics } from '../../hooks/useHaptics.ts';
import { useSettings } from '../../lib/settings.tsx';
import { fromMetres, panelMarks, toMetres, unitLabel, unitName } from '../../lib/units.ts';

interface Props {
    /** Point de départ EN MÈTRES : la distance de référence du club, ou la
     *  dernière mesurée. Le joueur corrige de quelques crans plutôt que de tout
     *  saisir. */
    initial: number;
    /** Reçoit des MÈTRES, quelle que soit l'unité affichée. */
    onValidate: (distanceM: number) => void;
    onSkip: () => void;
}

/** Saisie de distance sans clavier virtuel.
 *
 *  Le clavier du téléphone est l'ennemi de cet écran : il recouvre la moitié de
 *  l'interface, demande de viser des touches de 6 mm et impose de valider. Deux
 *  gros incréments et quelques raccourcis suffisent, parce que la valeur de
 *  départ est déjà proche — c'est la distance de référence du club. */
export function DistanceStepper({ initial, onValidate, onSkip }: Props) {
    const { settings, unit } = useSettings();
    const step = settings.distanceStepM;
    // On compte dans l'unité AFFICHÉE : « +5 » doit valoir cinq crans de ce que
    // le joueur lit, pas cinq mètres traduits en 5,47 yards. La conversion se
    // fait au moment d'enregistrer, et le stockage reste en mètres.
    const [value, setValue] = useState(() => fromMetres(initial, unit));
    /** Ce que le champ AFFICHE, qui peut être vide le temps d'une frappe. */
    const [draft, setDraft] = useState(() => String(fromMetres(initial, unit)));
    const [typing, setTyping] = useState(false);
    const { vibrate } = useHaptics();

    const bump = (delta: number): void => {
        vibrate('tap');
        setValue((v) => Math.min(450, Math.max(1, v + delta)));
    };

    /** Un panneau : la valeur est celle qu'on a lue, il n'y a rien à revoir.
     *
     *  Enregistre donc sans passer par « Valider ». Le second appui n'apporte
     *  aucune vérification — le nombre touché EST le nombre voulu — et coûte un
     *  geste sur un écran qu'on manipule debout, gant à la main. Une erreur se
     *  répare de toute façon : les coups sont modifiables. */
    const pick = (displayValue: number): void => {
        vibrate('success');
        onValidate(toMetres(displayValue, unit));
    };

    return (
        <div className="space-y-3">
            <p className="text-center text-sm opacity-60">Distance du coup</p>

            {/* Le chemin le plus court, donc le plus haut : au practice, on lit
                un panneau bien plus souvent qu'on ne mesure. */}
            <div>
                <div className="grid grid-cols-4 gap-2">
                    {panelMarks(unit).map((v) => (
                        <button
                            key={v}
                            type="button"
                            onClick={() => pick(v)}
                            className="btn btn-neutral h-14 tabular-nums text-lg font-bold"
                            aria-label={`Enregistrer ${v} ${unitName(unit)}`}
                        >
                            {v}
                        </button>
                    ))}
                </div>
                <p className="mt-1.5 text-center text-xs opacity-50">
                    Panneaux du practice, en {unitName(unit)} — un appui enregistre.
                </p>
            </div>

            {/* Le sélecteur passe en second.
                Il régnait sur cet écran quand il était le seul moyen d'entrer
                une distance ; il ne sert plus qu'au cas où la balle tombe ENTRE
                deux panneaux. Garder ses cercles de 80 px et son nombre géant
                repoussait « Valider » sous la ligne de flottaison d'un iPhone
                SE — mesuré, pas supposé.

                Les gros sauts ±25 disparaissent avec lui : l'échelle des
                panneaux fait désormais tous les déplacements grossiers, et
                mieux, puisqu'elle arrive sur une valeur ronde. */}
            <div className="flex items-center justify-center gap-4 pt-1">
                <button
                    type="button"
                    onClick={() => bump(-step)}
                    aria-label={`Retirer ${step} ${unitName(unit)}`}
                    className="btn btn-neutral size-14 shrink-0 rounded-full"
                >
                    <Minus size={26} />
                </button>

                <div className="min-w-24 text-center">
                    <span className="text-3xl font-bold tabular-nums text-primary">{value}</span>
                    <span className="ml-1 text-sm opacity-60">{unitLabel(unit)}</span>
                </div>

                <button
                    type="button"
                    onClick={() => bump(step)}
                    aria-label={`Ajouter ${step} ${unitName(unit)}`}
                    className="btn btn-neutral size-14 shrink-0 rounded-full"
                >
                    <Plus size={26} />
                </button>
            </div>

            {/* Le clavier, à la demande.
                Les incréments ne tombent pas sur 72 quand le pas est de 5, et
                s'acharner sur « − » pour une valeur exacte est pire que de
                taper trois chiffres. Il reste caché par défaut : sur un tapis,
                le clavier mange la moitié de l'écran. */}
            {typing && (
                <div className="flex items-center justify-center gap-2">
                    {/* Le champ garde sa propre CHAÎNE, distincte du nombre.
                        Lié directement au nombre, il ne pouvait jamais être
                        vide : tout effacer rendait « 0 », et le zéro restant
                        se collait devant la frappe suivante — on tapait 2 en
                        voulant 20 et on lisait « 02 ». On laisse donc le champ
                        se vider, et c'est à la sortie qu'on retombe sur un
                        nombre valide. */}
                    <input
                        type="text"
                        inputMode="numeric"
                        autoFocus
                        className="input input-bordered w-32 text-center text-2xl font-bold tabular-nums"
                        value={draft}
                        onFocus={(e) => e.currentTarget.select()}
                        onChange={(e) => {
                            // Chiffres seulement, et pas plus de trois : la
                            // borne haute est 450, et un champ qui accepte
                            // n'importe quoi oblige à corriger après coup.
                            const brut = e.target.value.replace(/\D/g, '').slice(0, 3);
                            setDraft(brut);
                            const n = Number(brut);
                            if (brut !== '' && n >= 1) setValue(Math.min(450, n));
                        }}
                        onBlur={() => setDraft(String(value))}
                        onKeyDown={(e) => { if (e.key === 'Enter') { setDraft(String(value)); setTyping(false); } }}
                    />
                    <button type="button" className="btn btn-ghost btn-sm"
                        onClick={() => setTyping(false)}>OK</button>
                </div>
            )}

            <button type="button" onClick={() => onValidate(toMetres(value, unit))}
                className="btn btn-primary btn-block h-14 text-lg font-bold">
                Valider {value} {unitLabel(unit)}
            </button>

            {/* Les deux issues rares sur une seule ligne. Empilées, elles
                poussaient « Valider » sous la ligne de flottaison d'un écran de
                667 px — soit la seule chose qu'il ne faut jamais avoir à
                chercher sur cet écran. */}
            <div className="flex gap-2">
                {/* À l'ouverture, le champ repart de la valeur courante : on
                    vient souvent d'approcher au sélecteur et on veut juste
                    ajuster les unités. */}
                <button type="button"
                    onClick={() => { setDraft(String(value)); setTyping((t) => !t); }}
                    className="btn btn-ghost btn-sm flex-1 gap-2">
                    <Keyboard size={16} /> Valeur exacte
                </button>
                <button type="button" onClick={onSkip} className="btn btn-ghost btn-sm flex-1">
                    Sans mesure
                </button>
            </div>
        </div>
    );
}
